import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import Modal from '../layouts/Modal';
import { UpdateEventModel } from '../../types/event';
import { getEvent, updateEvent } from '../../services/eventService';
import Button from '../misc/Button';
import ToggleSwitch from '../misc/ToggleSwitch';
import DateTimePicker from '../forms/DateTimePicker';

interface EditEventModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  eventId: number | null;
  onSuccess?: () => void;
}

export default function EditEventModal({ open, setOpen, eventId, onSuccess }: EditEventModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [form, setForm] = useState<Partial<UpdateEventModel>>({
    title: '',
    description: '',
    location: '',
    locationUrl: undefined,
    onlineLocation: false,
    startTime: undefined as any,
    endTime: undefined as any,
    maximumAttendees: undefined,
    type: undefined as any,
    attendanceRequired: false,
  });

  useEffect(() => {
    if (!open) return;
    const fetchEvent = async () => {
      if (!eventId) return;
      setInitializing(true);
      try {
        const ev: any = (await getEvent(eventId)) as unknown as any;
        setForm({
          title: ev.title,
          description: ev.description,
          location: ev.location || '',
          locationUrl: ev.locationUrl,
          onlineLocation: ev.onlineLocation,
          startTime: ev.startTime ? new Date(ev.startTime) : undefined,
          endTime: ev.endTime ? new Date(ev.endTime) : undefined,
          maximumAttendees: ev.maximumAttendees,
          type: ev.type,
          attendanceRequired: ev.attendanceRequired,
        });
      } catch (err) {
        console.error('Failed to load event for editing', err);
        toast.error(t('page.dashboard.event.loading'));
        setOpen(false);
      } finally {
        setInitializing(false);
      }
    };

    fetchEvent();
  }, [open, eventId, t, setOpen]);

  const handleSave = async () => {
    if (!eventId) return;
    if (!form.title || !form.startTime) {
      toast.error(t('page.dashboard.event.errors.titleRequired'));
      return;
    }

    setLoading(true);
    try {
      const updateModel: UpdateEventModel = {
        title: form.title!,
        description: form.description || '',
        location: form.location || '',
        locationUrl: form.locationUrl,
        onlineLocation: !!form.onlineLocation,
        startTime: form.startTime as Date,
        endTime: form.endTime as Date | undefined,
        maximumAttendees: form.maximumAttendees,
        type: form.type as any,
        dateOptions: [],
        customFields: [],
        attendanceRequired: form.attendanceRequired ?? false,
      } as any;

      await updateEvent(eventId, updateModel);
      toast.success(t('page.dashboard.tasks.updateSuccess'));
      setOpen(false);
      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to update event', err);
      toast.error(err?.response?.data?.message || t('page.dashboard.tasks.updateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} title={<>{t('page.editEvent.title')}</>}>
      {initializing ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-details">{t('general.loading')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-primary">{t('page.dashboard.event.title')}</label>
            <input
              type="text"
              value={form.title || ''}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-lg border-none !bg-secondary-50 p-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">
              {t('page.dashboard.event.description')}
            </label>
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full resize-none rounded-lg border-none p-2"
              rows={3}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">
              {t('page.dashboard.event.locationType')}
            </label>
            <ToggleSwitch
              leftLabel={t('page.dashboard.event.offlineLocation')}
              rightLabel={t('page.dashboard.event.onlineLocation')}
              value={!!form.onlineLocation}
              onChange={(value) => setForm({ ...form, onlineLocation: value })}
            />
          </div>

          {!form.onlineLocation ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-primary">
                {t('page.dashboard.event.location')}
              </label>
              <input
                type="text"
                value={form.location || ''}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full rounded-lg border-none !bg-secondary-50 p-2"
              />
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium text-primary">
                {t('page.dashboard.event.locationUrl')}
              </label>
              <input
                type="text"
                value={form.locationUrl || ''}
                onChange={(e) => setForm({ ...form, locationUrl: e.target.value })}
                className="w-full rounded-lg border-none !bg-secondary-50 p-2"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">{t('page.dashboard.event.startTime')}</label>
            <DateTimePicker
              value={form.startTime as Date | undefined}
              onChange={(date) => setForm({ ...form, startTime: date ?? undefined })}
              timeEnabled
              className="w-full"
              ariaLabel={t('page.dashboard.event.startTime')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">{t('page.dashboard.event.endTime')}</label>
            <DateTimePicker
              value={form.endTime as Date | undefined}
              onChange={(date) => setForm({ ...form, endTime: date ?? undefined })}
              timeEnabled
              className="w-full"
              ariaLabel={t('page.dashboard.event.endTime')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-primary">
              {t('page.dashboard.event.maximumAttendees')}
            </label>
            <input
              type="number"
              min="1"
              value={form.maximumAttendees || ''}
              onChange={(e) =>
                setForm({ ...form, maximumAttendees: e.target.value ? parseInt(e.target.value, 10) : undefined })
              }
              className="w-full rounded-lg border-none !bg-secondary-50 p-2"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-primary">
              {t('page.dashboard.event.attendanceRequired')}
            </label>
            <ToggleSwitch
              leftLabel={t('general.no')}
              rightLabel={t('general.yes')}
              value={!!form.attendanceRequired}
              onChange={(value) => setForm({ ...form, attendanceRequired: value })}
            />
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)} className="px-6 py-2">
              {t('general.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={loading}
              className={classNames('px-6 py-2')}>
              {loading ? t('general.saving') : t('general.save')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
